import type { TFunction } from 'i18next';
import type {
  InterventionRoomPropertyResponse,
  InterventionRoomResponse
} from '../../api/generated';
import { getRoomNameColumn, getRoomNeedsColumn, getRowActionColumns } from '../UI';
import type { RoomEditingCallbacks, TableColumn } from '../UI';
import { INTERVENTION_ROOM_NAME_COLUMN_KEY } from './interventionRoomsConstants';
import { OptionalNumberCell } from './OptionalNumberCell';
import columnStyles from './interventionRoomColumns.module.css';

interface InterventionRoomColumnCallbacks extends RoomEditingCallbacks<InterventionRoomResponse> {
  editingCapacityLimit: string;
  editingPriority: string;
  onCapacityLimitChange: (value: string) => void;
  onPriorityChange: (value: string) => void;
  allRoomProperties: InterventionRoomPropertyResponse[];
}

export const getInterventionRoomColumns = (
  t: TFunction,
  cb: InterventionRoomColumnCallbacks
): TableColumn<InterventionRoomResponse>[] => {
  const columns: TableColumn<InterventionRoomResponse>[] = [
    getRoomNameColumn(cb, {
      key: INTERVENTION_ROOM_NAME_COLUMN_KEY,
      header: t('settings-col-intervention-room-name')
    }),
    {
      key: 'capacityLimit',
      header: t('settings-col-capacity-limit'),
      width: 150,
      cellStyle: { whiteSpace: 'nowrap' },
      render: (room) => (
        <OptionalNumberCell
          value={room.capacityLimit}
          emptyLabel={t('capacity-limit-none')}
          isEditing={cb.editingRoomId === room.id}
          editingValue={cb.editingCapacityLimit}
          onChange={cb.onCapacityLimitChange}
          onSave={cb.onSaveName}
          onCancel={cb.onCancelEdit}
        />
      )
    },
    {
      key: 'priority',
      header: t('settings-col-room-priority'),
      width: 120,
      cellStyle: { whiteSpace: 'nowrap' },
      render: (room) => (
        <OptionalNumberCell
          value={room.priority}
          emptyLabel={t('room-priority-none')}
          isEditing={cb.editingRoomId === room.id}
          editingValue={cb.editingPriority}
          onChange={cb.onPriorityChange}
          onSave={cb.onSaveName}
          onCancel={cb.onCancelEdit}
        />
      )
    },
    getRoomNeedsColumn(cb, {
      header: t('settings-col-intervention-room-needs'),
      emptyLabel: t('intervention-room-need-no-results'),
      assignedProperties: (room) => room.interventionRoomProperties,
      allProperties: cb.allRoomProperties
    })
  ];

  if (!cb.canWrite) return columns;

  return [...columns, ...getRowActionColumns(t, cb, columnStyles.actionIcon)];
};
